/**
 * Standard Sales & Jobs API client — SPEC-51.
 *
 * The standard operational vertical: enquiry, quotation, job, invoice. This is
 * what a tenant runs unless they have asked for an operational vertical of their
 * own.
 *
 * NEUTRAL, DELIBERATELY. Nothing here names another vertical or borrows its
 * vocabulary. Shane, 2026-08-29: *"the standard area needs to be clean... it has
 * to be neutral"*.
 *
 * STAGE 2 — the enquiry. These MOVED here from `enquiriesQuotationsApi` and were
 * repointed at `/api/standard-sales/enquiries`. A move, not a copy: nothing else
 * called them, and `enquiries` is this section's own table.
 *
 * WHAT DELIBERATELY DID NOT COME. The travelling-folder helpers stayed where they
 * were. The folder is shared platform infrastructure - like attachments or the
 * audit log - rather than any one vertical's concept, and it has other callers.
 */

import { apiFetch, fetchAuthedBlob } from './api';
import type { CustomerCreditStanding } from './creditStandingApi';

export type EnquirySource = 'manual' | 'section_17' | 'email' | 'phone' | 'portal';
export type EnquiryStatus = 'new' | 'assigned' | 'converted' | 'won' | 'lost' | 'no_action';

export interface EnquiryPublic {
  id: string;
  /**
   * Current documents in this enquiry's travelling folder.
   *
   * Zero when it has no project yet — a folder appears when the first document
   * is filed, not when the enquiry is raised. Superseded revisions are not
   * counted: nine would mean three documents and six older versions of them.
   */
  docCount: number;
  reference: string;
  source: EnquirySource;
  sourceSectionRef: string | null;
  companyName: string;
  /**
   * The CRM customer this enquiry is for. Resolved from the typed company
   * name when it is raised, adding the company as a prospect if the CRM has
   * never heard of it. Null on rows raised before 2026-09-01.
   */
  customerId: string | null;
  /**
   * What accounts would say about this customer right now - the same numbers
   * the invoice gate refuses on. Null when there is no customer or it no
   * longer resolves, which is not the same as nothing owed.
   */
  creditStanding: CustomerCreditStanding | null;
  contactName: string;
  contactEmail: string | null;
  contactPhone: string | null;
  siteLocation: string | null;
  category: string;
  description: string;
  estimatedValue: string | null;
  requiredByDate: string | null;
  assignedToId: string | null;
  assignedToName: string | null;
  assignedAt: string | null;
  status: EnquiryStatus;
  convertedToQuotationId: string | null;
  convertedQuotationReference: string | null;
  convertedAt: string | null;
  notes: string | null;
  projectId: string | null;
  createdAt: string;
}

export interface EnquiryListResult {
  enquiries: EnquiryPublic[];
  total: number;
}

export interface EnquiryStatsResult {
  newUnassigned: number;
  assigned: number;
  convertedThisMonth: number;
  pipelineEstValue: string;
}

export interface SalesEngineerOption {
  id: string;
  name: string;
  role: string;
}

export type PipelineColumn = 'new' | 'allocated' | 'quote' | 'pipeline' | 'won' | 'lost';

export interface PipelineCardPublic {
  id: string;
  company: string;
  reference: string;
  value: number;
  assignedTo: string | null;
  column: PipelineColumn;
  daysInColumn: number;
}

export interface PipelineResult {
  cards: PipelineCardPublic[];
  stats: {
    pipelineValue: number;
    quoteValue: number;
    wonYtd: number;
    needsChasing: number;
  };
}

export interface CreateEnquiryPayload {
  source?: EnquirySource;
  sourceSectionRef?: string | null;
  companyName: string;
  contactName: string;
  contactEmail?: string | null;
  contactPhone?: string | null;
  siteLocation?: string | null;
  category: string;
  description: string;
  estimatedValue?: number | null;
  requiredByDate?: string | null;
  assignedToId?: string | null;
  notes?: string | null;
}

const BASE = '/api/standard-sales/enquiries';

/**
 * WHICH SECTION IS ASKING.
 *
 * Reagan, 2026-10-15: *"can we find out why if you put something on the front
 * desk its feeding the standard sales and jobs? this just needs to feed the
 * sales."*
 *
 * Sales and Standard Sales & Jobs are two views of one enquiries table. Every
 * screen below reads these same routes, so each one has to say which section
 * it is — otherwise a tenant running both sees every enquiry twice and the
 * post room feeds a pipeline it has nothing to do with.
 *
 * IT ALSO BELONGS IN THE QUERY KEY. Two screens asking the same route with
 * different sections under one key would be served each other's answer by the
 * cache, silently and only sometimes — the worst kind of wrong.
 */
export type OwningSection = 'standard_sales' | 'sales';

export async function fetchEnquiries(
  opts: {
    status?: EnquiryStatus | '';
    category?: string;
    search?: string;
    owningSection?: OwningSection;
  } = {},
  signal?: AbortSignal,
): Promise<EnquiryListResult> {
  return apiFetch<EnquiryListResult>(BASE, {
    method: 'GET',
    query: {
      status: opts.status || null,
      category: opts.category || null,
      search: opts.search || null,
      owningSection: opts.owningSection ?? null,
    },
    signal,
  });
}

export async function fetchEnquiryStats(signal?: AbortSignal): Promise<EnquiryStatsResult> {
  return apiFetch<EnquiryStatsResult>(`${BASE}/stats`, { method: 'GET', signal });
}

/**
 * The unassigned count the sidebar wears.
 *
 * Counts `enquiries` at status `new` — this section's own table. It moved here
 * with everything else rather than staying on a section that was about to be
 * retired.
 *
 * THE SALES ROW ASKS FOR A SECTION; THE JOBS ROW DOES NOT (2026-10-15).
 *
 * Called with no argument it counts every unassigned enquiry, which is what
 * it has always done and what Standard Sales & Jobs still wants — Reagan
 * asked for that section to be left exactly as it was. Sales passes 'sales'
 * so its own red number means its own work.
 */
export async function fetchEnquiryBadge(
  owningSection?: OwningSection,
  signal?: AbortSignal,
): Promise<number> {
  const data = await apiFetch<{ count: number }>(`${BASE}/badge`, {
    method: 'GET',
    query: { owningSection: owningSection ?? null },
    signal,
  });
  return data.count;
}

export async function fetchPipeline(signal?: AbortSignal): Promise<PipelineResult> {
  return apiFetch<PipelineResult>(`${BASE}/pipeline`, { method: 'GET', signal });
}

export async function fetchSalesEngineers(signal?: AbortSignal): Promise<SalesEngineerOption[]> {
  const data = await apiFetch<{ salesEngineers: SalesEngineerOption[] }>(
    `${BASE}/sales-engineers`,
    { method: 'GET', signal },
  );
  return data.salesEngineers;
}

export async function createEnquiry(payload: CreateEnquiryPayload): Promise<EnquiryPublic> {
  return apiFetch<EnquiryPublic>(BASE, { method: 'POST', body: payload });
}

export async function updateEnquiry(
  id: string,
  payload: Partial<CreateEnquiryPayload>,
): Promise<void> {
  await apiFetch<unknown>(`${BASE}/${id}`, { method: 'PUT', body: payload });
}

/**
 * Remove an enquiry.
 *
 * Soft on the server — the row stays, stamped `deletedAt`, so it is recoverable
 * and still auditable. Refused with 409 once converted, because the quotation it
 * became would be left pointing at nothing.
 */
export async function deleteEnquiry(id: string): Promise<void> {
  await apiFetch<unknown>(`${BASE}/${id}`, { method: 'DELETE' });
}

export async function assignEnquiry(id: string, assignedToId: string): Promise<EnquiryPublic> {
  return apiFetch<EnquiryPublic>(`${BASE}/${id}/assign`, {
    method: 'PUT',
    body: { assignedToId },
  });
}

/* ───────────────────────────── Quotations ─────────────────────────────
 *
 * SPEC-51 Stage 3. MOVED here from `enquiriesQuotationsApi` and repointed at
 * `/api/standard-sales/quotations`. A move, not a copy - `quotations` and its
 * part / labour / misc line tables are this section's own.
 *
 * The quote's travelling folder did NOT come. It is shared platform
 * infrastructure with other callers, so it stayed where it was.
 */

const QUOTES = '/api/standard-sales/quotations';

export type QuotationStatus =
  | 'draft'
  | 'sent'
  | 'revised'
  | 'negotiating'
  | 'won'
  | 'lost'
  | 'expired'
  | 'cancelled';

/*
 * One value, because a standard quote is lines and a price. Trade-specific
 * product types arrived here when this file moved out of the old base section
 * and have been removed - a section that has not asked for a manufacturing
 * vertical should not be offered one's vocabulary.
 */
export type SubQuotationType = 'general';
export type SubQuotationStatus = 'draft' | 'complete';

export interface QuotationPartLinePublic {
  id: string;
  stockItemId: string | null;
  description: string;
  quantity: number;
  unitCost: number;
  lineTotal: number;
  position: number;
}
export interface QuotationLabourLinePublic {
  id: string;
  description: string;
  category: string;
  hours: number;
  ratePerHour: number;
  lineTotal: number;
  position: number;
}
export interface QuotationMiscLinePublic {
  id: string;
  description: string;
  category: string;
  quantity: number;
  unitCost: number;
  lineTotal: number;
  position: number;
}
export interface SubQuotationPublic {
  id: string;
  number: number;
  type: SubQuotationType;
  size: string | null;
  variant: string | null;
  status: SubQuotationStatus;
  valueExVat: number;
  proStockMode: boolean;
}
export interface QuotationSummary {
  id: string;
  reference: string;
  customerId: string;
  customerName: string;
  /**
   * What accounts would say about this customer right now - the same numbers
   * the invoice gate refuses on. Null when there is no customer or it no
   * longer resolves, which is not the same as nothing owed.
   */
  creditStanding: CustomerCreditStanding | null;
  description: string;
  version: number;
  priceExVat: number;
  marginPct: number;
  effectiveMarginPct: number;
  assignedToName: string | null;
  validUntilDate: string;
  status: QuotationStatus;
  jobRef: string | null;
  /** 2026-10-14 — which editor opens it. See the Sales quote service. */
  pricingMode: string;
  billingPeriod: string;
  createdAt: string;
}
export interface QuotationDetail extends QuotationSummary {
  enquiryId: string | null;
  enquiryReference: string | null;
  siteLocation: string;
  partsSubtotal: number;
  labourSubtotal: number;
  miscSubtotal: number;
  directCostTotal: number;
  overheadPct: number;
  overheadAmount: number;
  totalCost: number;
  vatRate: number;
  vatAmount: number;
  priceIncVat: number;
  grossProfit: number;
  notes: string | null;
  internalNotes: string | null;
  sentAt: string | null;
  wonAt: string | null;
  lostAt: string | null;
  lostReason: string | null;
  partLines: QuotationPartLinePublic[];
  labourLines: QuotationLabourLinePublic[];
  miscLines: QuotationMiscLinePublic[];
  subQuotations: SubQuotationPublic[];
}
export interface QuotationStats {
  pipelineValueExVat: number;
  sentThisMonth: number;
  sentThisMonthAvgMargin: number;
  wonThisMonth: number;
  wonThisMonthContractValue: number;
  expiringSoon: number;
}
export interface QuotationListResult {
  quotations: QuotationSummary[];
  total: number;
  stats: QuotationStats;
}
export interface QuotationCustomerRow {
  id: string;
  companyName: string;
  openQuoteCount: number;
  wonCount: number;
  totalWonExVat: number;
  lastQuoteAt: string | null;
}

export interface CreateQuotationPayload {
  customerId: string;
  description: string;
  siteLocation?: string;
  enquiryId?: string | null;
  pricingMode?: "cost_build_up" | "direct";
  billingPeriod?: "one_off" | "monthly" | "annual";
  validUntilDays?: number;
  overheadPct?: number;
  marginPct?: number;
  notes?: string | null;
  internalNotes?: string | null;
  /**
   * Which list it belongs on. Needed ONLY by the "New quotation" button,
   * which raises a quote with no enquiry behind it — everything raised FROM
   * an enquiry takes that enquiry's section on the server and anything sent
   * here is ignored.
   */
  owningSection?: OwningSection;
}
export interface UpdateQuotationPayload {
  description?: string;
  siteLocation?: string;
  validUntilDate?: string;
  overheadPct?: number;
  marginPct?: number;
  notes?: string | null;
  internalNotes?: string | null;
}
export interface PartLinePayload {
  stockItemId?: string | null;
  description: string;
  quantity: number;
  unitCost: number;
}
export interface LabourLinePayload {
  description: string;
  category: string;
  hours: number;
  ratePerHour: number;
}
export interface MiscLinePayload {
  description: string;
  category?: string;
  quantity: number;
  unitCost: number;
}

/**
 * ONLY SALES ASKS FOR A SECTION.
 *
 * Reagan, 15 Oct 2026: *"the quoteations in sales bit in this branch just
 * needs to be seen in sales. no need to touch the quotes in standard and sub
 * contract and switchboard and travelling folders."* So the Sales list sends
 * `owningSection`, Standard Sales & Jobs sends nothing and still gets every
 * quote, and the three services that fetch a quote by id are untouched.
 */
export async function fetchQuotations(
  opts: {
    status?: QuotationStatus | '';
    customerId?: string;
    search?: string;
    owningSection?: OwningSection;
  } = {},
  signal?: AbortSignal,
): Promise<QuotationListResult> {
  return apiFetch<QuotationListResult>(`${QUOTES}`, {
    method: 'GET',
    query: {
      status: opts.status || null,
      customerId: opts.customerId || null,
      search: opts.search || null,
      owningSection: opts.owningSection ?? null,
    },
    signal,
  });
}
export async function fetchQuotation(id: string, signal?: AbortSignal): Promise<QuotationDetail> {
  return apiFetch<QuotationDetail>(`${QUOTES}/${id}`, { method: 'GET', signal });
}
export async function fetchQuotationCustomers(
  opts: { search?: string } = {},
  signal?: AbortSignal,
): Promise<QuotationCustomerRow[]> {
  /*
   * Standard's OWN endpoint, not the base section's. This one call was left
   * pointing at `/api/quotations/customers` when the rest of the file moved to
   * `/api/standard-sales/quotations`, which is gated on `enquiries_quotations`
   * - so the Customers tab would have 403'd for a tenant holding only
   * `standard_sales`, which is precisely the tenant this section exists for.
   */
  const data = await apiFetch<{ customers: QuotationCustomerRow[] }>(`${QUOTES}/customers`, {
    method: 'GET',
    query: { search: opts.search || null },
    signal,
  });
  return data.customers;
}
export async function createQuotation(payload: CreateQuotationPayload): Promise<QuotationDetail> {
  return apiFetch<QuotationDetail>(`${QUOTES}`, { method: 'POST', body: payload });
}
export async function updateQuotation(id: string, payload: UpdateQuotationPayload): Promise<void> {
  await apiFetch<unknown>(`${QUOTES}/${id}`, { method: 'PUT', body: payload });
}
/** Line mutations return both the new/updated line AND the full refreshed
 * quotation so the client can setQueryData without a round-trip refetch. */
export interface LineMutationResponse<L> {
  line: L;
  quotation: QuotationDetail;
}
export interface QuotationOnlyResponse {
  quotation: QuotationDetail;
}

export async function addPartLine(
  id: string,
  payload: PartLinePayload,
): Promise<LineMutationResponse<QuotationPartLinePublic>> {
  return apiFetch<LineMutationResponse<QuotationPartLinePublic>>(
    `${QUOTES}/${id}/lines/parts`,
    { method: 'POST', body: payload },
  );
}
export async function updatePartLine(
  id: string,
  lineId: string,
  payload: Partial<PartLinePayload>,
): Promise<QuotationOnlyResponse> {
  return apiFetch<QuotationOnlyResponse>(
    `${QUOTES}/${id}/lines/parts/${lineId}`,
    { method: 'PUT', body: payload },
  );
}
export async function deletePartLine(id: string, lineId: string): Promise<QuotationOnlyResponse> {
  return apiFetch<QuotationOnlyResponse>(
    `${QUOTES}/${id}/lines/parts/${lineId}`,
    { method: 'DELETE' },
  );
}
export async function addLabourLine(
  id: string,
  payload: LabourLinePayload,
): Promise<LineMutationResponse<QuotationLabourLinePublic>> {
  return apiFetch<LineMutationResponse<QuotationLabourLinePublic>>(
    `${QUOTES}/${id}/lines/labour`,
    { method: 'POST', body: payload },
  );
}
export async function updateLabourLine(
  id: string,
  lineId: string,
  payload: Partial<LabourLinePayload>,
): Promise<QuotationOnlyResponse> {
  return apiFetch<QuotationOnlyResponse>(
    `${QUOTES}/${id}/lines/labour/${lineId}`,
    { method: 'PUT', body: payload },
  );
}
export async function deleteLabourLine(id: string, lineId: string): Promise<QuotationOnlyResponse> {
  return apiFetch<QuotationOnlyResponse>(
    `${QUOTES}/${id}/lines/labour/${lineId}`,
    { method: 'DELETE' },
  );
}
export async function addMiscLine(
  id: string,
  payload: MiscLinePayload,
): Promise<LineMutationResponse<QuotationMiscLinePublic>> {
  return apiFetch<LineMutationResponse<QuotationMiscLinePublic>>(
    `${QUOTES}/${id}/lines/misc`,
    { method: 'POST', body: payload },
  );
}
export async function updateMiscLine(
  id: string,
  lineId: string,
  payload: Partial<MiscLinePayload>,
): Promise<QuotationOnlyResponse> {
  return apiFetch<QuotationOnlyResponse>(
    `${QUOTES}/${id}/lines/misc/${lineId}`,
    { method: 'PUT', body: payload },
  );
}
export async function deleteMiscLine(id: string, lineId: string): Promise<QuotationOnlyResponse> {
  return apiFetch<QuotationOnlyResponse>(
    `${QUOTES}/${id}/lines/misc/${lineId}`,
    { method: 'DELETE' },
  );
}
export async function createSubQuotation(
  id: string,
  payload: { number?: number; type?: SubQuotationType; size?: string | null; variant?: string | null } = {},
): Promise<SubQuotationPublic> {
  return apiFetch<SubQuotationPublic>(`${QUOTES}/${id}/subquotes`, { method: 'POST', body: payload });
}
export async function updateSubQuotation(
  id: string,
  sqId: string,
  payload: {
    number?: number;
    type?: SubQuotationType;
    size?: string | null;
    variant?: string | null;
    valueExVat?: number;
    status?: SubQuotationStatus;
  },
): Promise<void> {
  await apiFetch<unknown>(`${QUOTES}/${id}/subquotes/${sqId}`, { method: 'PUT', body: payload });
}
export async function deleteSubQuotation(id: string, sqId: string): Promise<void> {
  await apiFetch<unknown>(`${QUOTES}/${id}/subquotes/${sqId}`, { method: 'DELETE' });
}
/** The email a quotation goes out in. To and CC take several, separated by ; or ,. */
export interface QuotationEmail {
  to: string;
  cc: string;
  subject: string;
  body: string;
}
/** Emails the quotation from the company's own email; only then is it marked sent. */
export async function sendQuotation(id: string, email: QuotationEmail): Promise<void> {
  await apiFetch<unknown>(`${QUOTES}/${id}/send`, { method: 'POST', body: email });
}
/** The quotation as the PDF the customer receives. */
export function fetchQuotationPdf(id: string): Promise<Blob> {
  return fetchAuthedBlob(`${QUOTES}/${id}/pdf`);
}
export async function fetchQuotationEmailDefaults(
  id: string,
  signal?: AbortSignal,
): Promise<{ to: string; reference: string; customerName: string }> {
  return apiFetch(`${QUOTES}/${id}/email-defaults`, { method: 'GET', signal });
}
export async function winQuotation(id: string): Promise<{ jobRef: string }> {
  return apiFetch<{ jobRef: string }>(`${QUOTES}/${id}/win`, { method: 'POST' });
}
export async function reviseQuotation(id: string, revisionNotes: string): Promise<QuotationDetail> {
  return apiFetch<QuotationDetail>(`${QUOTES}/${id}/revise`, {
    method: 'POST',
    body: { revisionNotes },
  });
}


/* ───────────────────────────────── Jobs ─────────────────────────────────
 *
 * SPEC-51 Stage 4. Standard's own job surface, over the SHARED `jobs` table.
 *
 * NOT A MOVE, and it is the one part of this vertical that is not. Enquiries and
 * quotations moved outright because this section has tables of its own for them.
 * Jobs are different: the platform keeps ONE `jobs` table, and it stays that way.
 *
 * The TYPES come from `jobsApi` on purpose. A job is one entity whichever
 * vertical raised it, and declaring a second shape for the same row is how two
 * dialects of one record start. What Standard owns is the surface: its own
 * routes, its own gate, its own screen.
 */

import type { JobPublic, JobListResult, EngineerSummary, VariationOrderPublic, JobStageFlow } from './jobsApi';
import { afterHandoverApiAt } from './jobsApi';

export type { JobPublic, JobListResult, EngineerSummary };

/**
 * A change to the agreed work on a job. The same variation record the rest of
 * the platform reads, under the name this section's screen uses for it.
 */
export type JobChange = VariationOrderPublic;

const JOBS = '/api/standard-sales/jobs';

export async function fetchJobs(
  opts: { status?: string; search?: string } = {},
  signal?: AbortSignal,
): Promise<JobListResult> {
  return apiFetch<JobListResult>(JOBS, {
    method: 'GET',
    query: { status: opts.status || null, search: opts.search || null },
    signal,
  });
}

export async function fetchJob(id: string, signal?: AbortSignal): Promise<JobPublic> {
  return apiFetch<JobPublic>(`${JOBS}/${id}`, { method: 'GET', signal });
}

export async function fetchJobEngineers(signal?: AbortSignal): Promise<EngineerSummary[]> {
  return apiFetch<EngineerSummary[]>(`${JOBS}/engineers`, { method: 'GET', signal });
}

/** Issues after handover on a completed job, through this section's own routes. */
export const standardAfterHandoverApi = afterHandoverApiAt(JOBS);

/** The company's job stage flow: its sector's, else the built-in one. */
export async function fetchJobStageFlow(signal?: AbortSignal): Promise<JobStageFlow> {
  return apiFetch<JobStageFlow>(`${JOBS}/stage-flow`, { method: 'GET', signal });
}

/**
 * Move a job to another stage.
 *
 * `notes` is required, and the server refuses without it. A stage moved with no
 * reason recorded is a gap in the job's history, and the history is what an
 * audit reads — so the client does not offer a way to skip it either.
 */
export async function changeJobStage(
  id: string,
  stage: string,
  notes: string,
): Promise<JobPublic> {
  return apiFetch<JobPublic>(`${JOBS}/${id}/stage`, {
    method: 'PUT',
    body: { stage, notes },
  });
}

export async function assignJobStaff(
  id: string,
  input: { leadEngineerId?: string | null; assignedStaff?: string[] },
): Promise<JobPublic> {
  return apiFetch<JobPublic>(`${JOBS}/${id}/staff`, { method: 'PUT', body: input });
}

/*
 * Changes to a job (ISO 9001 clause 8.5.6).
 *
 * Raising a change moves nothing. Approving one moves the job's value by the
 * change and records the clause; rejecting one does neither. A decision is
 * final: the server refuses a second one, and a wrong change is put right by
 * raising another.
 */

/** Changes raised against a job, oldest first. */
export async function fetchJobChanges(jobId: string, signal?: AbortSignal): Promise<JobChange[]> {
  return apiFetch<JobChange[]>(`${JOBS}/${jobId}/changes`, { method: 'GET', signal });
}

export interface RaiseJobChangeInput {
  /** What is changing about the agreed work. */
  description: string;
  /** Who asked for it. */
  instructedBy: string;
  /** The day they asked, as YYYY-MM-DD. */
  instructedAt: string;
  /** The effect on the job's value before tax. Negative for a reduction, 0 for none. */
  value: number;
}

export async function raiseJobChange(jobId: string, input: RaiseJobChangeInput): Promise<JobChange> {
  return apiFetch<JobChange>(`${JOBS}/${jobId}/changes`, { method: 'POST', body: input });
}

export async function decideJobChange(
  jobId: string,
  changeId: string,
  input: { decision: 'approve' | 'reject'; reference?: string | null },
): Promise<JobChange> {
  return apiFetch<JobChange>(`${JOBS}/${jobId}/changes/${changeId}/decision`, {
    method: 'PUT',
    body: input,
  });
}

/* ────────────────────────────── The intake queue ──────────────────────────
 *
 * SPEC-51 Stage 7. Where work arrives before it is an enquiry: an email, a
 * drawing, a scope of works.
 *
 * NO AUTO-IMPORT. Nothing parses a file and creates a record. A person opens it,
 * reads it, and raises the enquiry themselves — the queue records what they
 * decided. Over its own table.
 */

export type IntakeStatus = 'queued' | 'reviewed' | 'imported' | 'rejected';

export interface IntakeFilePublic {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  status: IntakeStatus;
  reviewNotes: string | null;
  enquiryId: string | null;
  enquiryReference: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
}

const INTAKE = '/api/standard-sales/intake';

export async function fetchIntake(
  opts: { status?: IntakeStatus | '' } = {},
  signal?: AbortSignal,
): Promise<{ files: IntakeFilePublic[]; queuedCount: number }> {
  return apiFetch<{ files: IntakeFilePublic[]; queuedCount: number }>(INTAKE, {
    method: 'GET',
    query: { status: opts.status || null },
    signal,
  });
}

/**
 * Drop a file into the queue.
 *
 * The same file twice returns the existing row rather than a duplicate —
 * matched on content, not filename. Re-dropping is somebody unsure whether it
 * landed, not a mistake to be told off for.
 */
export async function uploadIntakeFile(file: File): Promise<IntakeFilePublic> {
  const fd = new FormData();
  fd.append('file', file);
  return apiFetch<IntakeFilePublic>(INTAKE, { method: 'POST', body: fd });
}

export function intakeFileUrl(id: string): string {
  return `${INTAKE}/${id}/file`;
}

export async function reviewIntakeFile(
  id: string,
  status: 'reviewed' | 'rejected',
  notes: string | null,
): Promise<IntakeFilePublic> {
  return apiFetch<IntakeFilePublic>(`${INTAKE}/${id}/review`, {
    method: 'PUT',
    body: { status, notes },
  });
}

/** The only route to `imported` — so the status and the link cannot disagree. */
export async function linkIntakeToEnquiry(
  id: string,
  enquiryId: string,
): Promise<IntakeFilePublic> {
  return apiFetch<IntakeFilePublic>(`${INTAKE}/${id}/enquiry`, {
    method: 'PUT',
    body: { enquiryId },
  });
}

export async function removeIntakeFile(id: string): Promise<void> {
  await apiFetch<unknown>(`${INTAKE}/${id}`, { method: 'DELETE' });
}
